// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import TerceraMessageBoxTemplate from '@front/templates/TerceraMessageBoxTemplate.html';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { Resources } from '@shared/localizations/Resources';
import { messageBoxHandler } from '@shared/utils/AppHandlers';
import { TerceraButtonStyle } from '@shared/utils/Enums/ButtonEnums';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { setMessageBoxAsyncHandler } from '@shared/utils/MessageBoxUtils';
import { SoundManager } from '@shared/utils/SoundManager';
import { TerceraWindowBase } from './TerceraWindowBase';
import { ScreensNames } from '../UtilsClasses/FactoryConstants';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
export class TerceraMessageBox extends TerceraWindowBase {
    constructor() {
        super();
        this.customDisposeHandler = null;
        this.OK_Handler = null;
        this.NO_Handler = null;
        this.onShowNextTimeChanged = (event) => {
            void this.set('showNextTime', event.currentTarget.checked);
        };
    }
    getType() {
        return ScreensNames.TerceraMessageBox;
    }
    oninit() {
        super.oninit();
        this.on('okClick', this.okClick);
        this.on('cancelClick', this.cancelClick);
        this.observe('messageBoxType', (newValue) => {
            let iconSvg = 'infotool';
            let infoCustomCss = '';
            switch (newValue) {
                case MessageBoxType.Info:
                    iconSvg = 'infotool';
                    break;
                case MessageBoxType.Question:
                    iconSvg = 'question';
                    break;
                case MessageBoxType.Error:
                case MessageBoxType.Warning:
                    iconSvg = 'menuError';
                    break;
                case MessageBoxType.Approve:
                    iconSvg = 'accept';
                    infoCustomCss = 'accept-info-tool';
                    break;
                case MessageBoxType.YellowInfo:
                    iconSvg = 'infotool';
                    infoCustomCss = 'yellow-info-tool';
                    break;
            }
            void this.set({ iconSvg, infoCustomCss });
        });
    }
    oncomplete() {
        super.oncomplete();
        this.center();
    }
    onGlobalKeyDown(code) {
        if (this.get('focused')) {
            if (code === KeyCode.ESC) {
                this.cancelClick();
            }
            if (code === KeyCode.ENTER && this.get('allowKeyboardConfirmation')) {
                this.okClick();
            }
        }
    }
    onteardown() {
        const keyProc = KeyEventProcessor;
        keyProc.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
    }
    okClick() {
        const okCallBack = this.get('okCallBack');
        if (!isNullOrUndefined(okCallBack)) {
            okCallBack(this.get('showNextTimeChB') ? this.get('showNextTime') : undefined);
        }
        // new Way
        if (!isNullOrUndefined(this.OkClicked)) {
            this.OkClicked();
        }
        this.close();
    }
    cancelClick() {
        const cancelCallBack = this.get('cancelCallBack');
        if (!isNullOrUndefined(cancelCallBack)) {
            cancelCallBack(this.get('showNextTimeChB') ? this.get('showNextTime') : undefined);
        }
        if (!isNullOrUndefined(this.CancelClicked)) {
            this.CancelClicked();
        }
        this.close();
    }
    // Close button (cross icon).
    onCloseButtonClick() {
        var _a;
        if (this.canCloseFromButton()) {
            this.close();
            const closeButtonCallBack = (_a = this.get('closeButtonCallBack')) !== null && _a !== void 0 ? _a : this.get('cancelCallBack');
            if (!isNullOrUndefined(closeButtonCallBack)) {
                closeButtonCallBack(this.get('showNextTimeChB') ? this.get('showNextTime') : undefined);
            }
        }
    }
    dispose() {
        if (!isNullOrUndefined(this.customDisposeHandler)) {
            this.customDisposeHandler();
        }
        super.dispose();
    }
    updateWithParams(headerText, bodyText, type, okCallBack, cancelCallBack, showNextTimeChB, hideCancel, errorText) {
        void this.set({
            messageBoxType: type,
            header: headerText,
            messageBoxText: bodyText,
            okCallBack,
            cancelCallBack,
            showNextTimeChB,
            hideCancel,
            showNextTimeText: Resources.getResource('general.messageBox.showNextTime'),
            okText: Resources.getResource('general.messageBox.yes'),
            cancelText: Resources.getResource('general.messageBox.no')
        });
        if (isValidString(errorText)) {
            void this.set({
                errorMode: true,
                errorText,
                showNextTimeChB: !!showNextTimeChB
            });
        }
    }
    updateWithAdditionalData(addData) {
        if (!isNullOrUndefined(addData)) {
            if (Object.prototype.hasOwnProperty.call(addData, 'forceCloseOnLogout') === true) {
                this.forceCloseOnLogout = addData.forceCloseOnLogout;
            }
            if (isValidString(addData.cancelText)) {
                void this.set({ cancelText: addData.cancelText });
            }
            if (isValidString(addData.okText)) {
                void this.set({ okText: addData.okText });
            }
            if (!isNullOrUndefined(addData.closeButtonCallBack)) {
                void this.set('closeButtonCallBack', addData.closeButtonCallBack);
            }
            if (Object.prototype.hasOwnProperty.call(addData, 'allowKeyboardConfirmation') === true) {
                void this.set('allowKeyboardConfirmation', addData.allowKeyboardConfirmation);
            }
            if (Object.prototype.hasOwnProperty.call(addData, 'cantActionBeforeClose') === true) {
                void this.set('showFullscreenCloud', Boolean(addData.cantActionBeforeClose));
            }
            if (Object.prototype.hasOwnProperty.call(addData, 'hideCloseBtn') === true) {
                void this.set('closeBtnVisible', addData.hideCloseBtn !== true);
            }
            if (Object.prototype.hasOwnProperty.call(addData, 'zIndex') === true) {
                void this.set('zIndex', addData.zIndex);
            }
            if (Object.prototype.hasOwnProperty.call(addData, 'btnOKStyle') === true) {
                void this.set('btnOKStyle', addData.btnOKStyle);
            }
            if (Object.prototype.hasOwnProperty.call(addData, 'btnCancelStyle') === true) {
                void this.set('btnCancelStyle', addData.btnCancelStyle);
            }
            if (Object.prototype.hasOwnProperty.call(addData, 'style_addition_header') === true) {
                void this.set('style_addition_header', addData.style_addition_header);
            }
        }
    }
    static PlaySound(type) {
        let soundKey = null;
        switch (type) {
            case MessageBoxType.Error:
            case MessageBoxType.Warning:
            case MessageBoxType.Info:
            case MessageBoxType.Question:
                soundKey = SoundManager.SoundKeys.Warning;
                break;
            default:
                soundKey = SoundManager.SoundKeys.Confirmation;
        }
        SoundManager.tryPlaySound(soundKey);
    }
    static MessageBoxHandlerInitialize() {
        messageBoxHandler.Show = TerceraMessageBox.Show;
        messageBoxHandler.msgType = MessageBoxType;
        setMessageBoxAsyncHandler(TerceraMessageBox.ShowAsync);
    }
    static Show(headerText, bodyText, type, okCallBack, cancelCallBack, showNextTimeChB, hideCancel, errorText, addData, skipOnTeardown = false, terceraMessageBox = new TerceraMessageBox()) {
        terceraMessageBox.updateWithParams(headerText, bodyText, type, okCallBack, cancelCallBack, showNextTimeChB, hideCancel, errorText);
        MainWindowManager.AddControl(terceraMessageBox);
        terceraMessageBox.updateWithAdditionalData(addData);
        terceraMessageBox.setFocus();
        const keyProc = KeyEventProcessor;
        keyProc.OnKeyDown.Subscribe(terceraMessageBox.onGlobalKeyDown, terceraMessageBox);
        terceraMessageBox.forceCloseOnLogout = !skipOnTeardown;
        terceraMessageBox.OK_Handler = new Promise(function (resolve) {
            terceraMessageBox.OkClicked = resolve;
        });
        terceraMessageBox.NO_Handler = new Promise(function (resolve) {
            terceraMessageBox.CancelClicked = resolve;
        });
        TerceraMessageBox.PlaySound(type);
        return terceraMessageBox;
    }
    static ShowAsync(messageBoxParameters) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve) => {
                const terceraMessageBox = new TerceraMessageBox();
                void terceraMessageBox.set({
                    header: messageBoxParameters.title,
                    messageBoxText: messageBoxParameters.message,
                    messageBoxType: messageBoxParameters.type,
                    okText: messageBoxParameters.okText,
                    cancelText: messageBoxParameters.cancelText,
                    hideCancel: !isValidString(messageBoxParameters.cancelText),
                    okCallBack: () => {
                        resolve(true);
                    },
                    cancelCallBack: () => {
                        resolve(false);
                    },
                    closeButtonCallBack: () => {
                        resolve(false);
                    }
                });
                MainWindowManager.AddControl(terceraMessageBox);
                terceraMessageBox.setFocus();
                terceraMessageBox.center();
                TerceraMessageBox.PlaySound(messageBoxParameters.type);
            });
        });
    }
}
TerceraWindowBase.extendWith(TerceraMessageBox, {
    data: function () {
        return {
            // width: 485, // Must be commented out or removed for the correct application of the TVTradingPlatform style
            errorTop: 0,
            header: '',
            resizable: false,
            showFooter: false,
            infoCustomCss: '',
            iconSvg: '',
            messageBoxText: '',
            showModalMask: true,
            showNextTimeText: 'Show next time',
            showNextTime: true,
            showNextTimeChB: false,
            okText: 'Yes',
            cancelText: 'No',
            btnOKStyle: TerceraButtonStyle.Blue,
            btnCancelStyle: TerceraButtonStyle.Standard,
            messageBoxType: null,
            okCallBack: null,
            cancelCallBack: null,
            closeButtonCallBack: null,
            hideCancel: false,
            allowKeyboardConfirmation: true,
            showFullscreenCloud: false, // TODO 83829
            closeBtnVisible: true,
            errorMode: false,
            errorText: '',
            style_addition_header: ''
        };
    },
    partials: { bodyPartial: TerceraMessageBoxTemplate }
});
